import React, { FunctionComponent } from 'react';
import { v1 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import {
    Box,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { BASE_URL } from '../../../../routes';
import TableView from '../../../../components/TableView';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import { Person } from '../../../../store/modules/person/types';
import { getFullAddress } from '../../../../utils/utils';
import { getPersonSimpleName } from '../../../../utils/utils';
import { Order } from '../../../../store/modules/application/types';

type PersonProps = {
    persons: Person[];
    sortBy: string;
    sortOrder: Order;
    setSortOrder: Function;
    setSortColumn: Function;
};

interface IColumn {
    id: 'name|string' | 'address|string';
    label: string;
    maxWidth?: number;
    align?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    field: string;
}
export interface IDetailProps {
    id:
        | 'contactType.name|string'
        | 'name|string'
        | 'address|address'
        | 'company|string'
        | 'phone|string'
        | 'fax|string'
        | 'email|string';
    label: string;
    maxWidth?: number;
    align?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    field: string;
}

const columns: IColumn[] = [
    {
        id: 'name|string',
        label: 'Name',
        align: 'left',
        maxWidth: 300,
        field: 'name',
    },
    {
        id: 'address|string',
        label: 'Address',
        align: 'left',
        maxWidth: 300,
        field: 'address',
    },
];

const contactColumns: IDetailProps[] = [
    {
        id: 'contactType.name|string',
        label: 'Type',
        align: 'left',
        maxWidth: 300,
        field: 'contactType.name',
    },
    {
        id: 'name|string',
        label: 'Name',
        align: 'left',
        maxWidth: 300,
        field: 'name',
    },
    {
        id: 'company|string',
        label: 'Company',
        align: 'left',
        maxWidth: 300,
        field: 'company',
    },
    {
        id: 'address|address',
        label: 'Address',
        align: 'left',
        maxWidth: 300,
        field: 'address',
    },
    {
        id: 'phone|string',
        label: 'Phone',
        align: 'left',
        maxWidth: 300,
        field: 'phone',
    },
    {
        id: 'fax|string',
        label: 'Fax',
        align: 'left',
        maxWidth: 300,
        field: 'fax',
    },
    {
        id: 'email|string',
        label: 'Email',
        align: 'left',
        maxWidth: 300,
        field: 'email',
    },
];

const useStyles = makeStyles({
    rowContainer: {
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    contactLabel: {
        fontSize: 16,
        color: '#484848',
    },
    headerColumn: {
        fontSize: 14,
        color: '#484848',
        fontWeight: 'bold',
    },
    rowColumn: {
        fontSize: 14,
        color: '#484848',
    },
});
const AppointmentPersons: FunctionComponent<PersonProps> = (props) => {
    /* State for modals */
    const classes = useStyles();
    const { persons, sortBy, sortOrder, setSortColumn, setSortOrder } = props;
    const history = useHistory();

    const personData = persons?.map((element) => {
        return {
            ...element,
            name: getPersonSimpleName(element.name),
            address: getFullAddress(element?.address),
        };
    });

    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/person/${id}/detail`);
    };

    const renderDetails = (row: Person) => {
        return (
            <>
                {row?.contacts?.length ? (
                    <>
                        <Box margin={1}>
                            <Typography variant="h4" gutterBottom component="div" className={classes.contactLabel}>
                                Contacts
                            </Typography>
                        </Box>
                        <Table size="small" aria-label="details">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headerColumn} width="10%">
                                        Type
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="20%">
                                        Name
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="20%">
                                        Company
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="20%">
                                        Contact Address
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="10%">
                                        Phone
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="10%">
                                        Fax
                                    </TableCell>
                                    <TableCell className={classes.headerColumn} width="10%">
                                        Email
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {row?.contacts?.map((row: { [x: string]: any; id: any }) => {
                                    return (
                                        <TableRow className={classes.rowContainer} hover tabIndex={-1} key={uuid()}>
                                            {contactColumns?.map((column: any) => {
                                                const valueObj: any = {
                                                    value: row,
                                                };
                                                const valueWithType = column?.id.split('|');
                                                const columnId = valueWithType[0];
                                                const columnType = valueWithType[1];
                                                const objectTree = columnId.split('.');
                                                if (objectTree.length > 0) {
                                                    objectTree.forEach((element: string) => {
                                                        valueObj.value = valueObj.value && valueObj.value[element];
                                                    });
                                                } else {
                                                    valueObj.value = row[columnId];
                                                }
                                                if (valueObj.value !== null && valueObj.value !== undefined) {
                                                    if (valueObj.value !== null && valueObj?.value !== undefined) {
                                                        switch (columnType) {
                                                            case 'address':
                                                                valueObj.value = getFullAddress(valueObj.value);
                                                                break;

                                                            default:
                                                                break;
                                                        }
                                                    }
                                                }
                                                return (
                                                    <TableCell
                                                        key={columnId}
                                                        align={column.align}
                                                        style={{
                                                            maxWidth: column.maxWidth,
                                                        }}
                                                        className={classes.rowColumn}
                                                    >
                                                        {valueObj.value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </>
                ) : (
                    <TableEmptyState title="No Contacts" message="All contacts will be listed here."></TableEmptyState>
                )}
            </>
        );
    };

    return (
        <>
            <Paper className="case-entries-container fullHeight">
                {personData && personData.length > 0 ? (
                    <TableView
                        columns={columns}
                        data={personData}
                        count={personData.length}
                        onClickRow={onClickRow}
                        deleteFlag={false}
                        getAppIcon={false}
                        editFlag={false}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                        renderDetails={renderDetails}
                        showDetailsLabel={'View Contacts'}
                    />
                ) : (
                    <TableEmptyState
                        title="No License Owners"
                        message="All licensees will be listed here."
                    ></TableEmptyState>
                )}
            </Paper>
        </>
    );
};

export default AppointmentPersons;
